/**
 * ScheduledJobWizard Component
 *
 * Multi-step wizard for creating and editing scheduled jobs.
 * Uses Standard Component Library throughout.
 */

import React, { useState, useEffect } from 'react';
import {
  StandardButton,
  StandardInput,
  StandardSelect,
  StandardCard,
  useToast
} from '../../standard';
import { ArrowLeft, ArrowRight, Save, X } from 'lucide-react';
import { scheduledJobsStorage } from '../../../services/scheduledJobsStorage';
import * as scheduledJobsStorageRouter from '../../../services/scheduledJobsStorageRouter';
import { ScheduledJob, JobFrequency, JobType } from '../../../types/scheduledJobs';
import { jobTypeDefinitions } from '../../../data/mockScheduledJobs';

type WizardStep = 'basic' | 'schedule' | 'configuration' | 'notifications';

interface ScheduledJobWizardProps {
  jobId?: string; // If provided, edit mode
  onSave: () => void;
  onCancel: () => void;
}

export const ScheduledJobWizard: React.FC<ScheduledJobWizardProps> = ({
  jobId,
  onSave,
  onCancel
}) => {
  const { success, error: showError } = useToast();
  const isEditMode = !!jobId;

  const [currentStep, setCurrentStep] = useState<WizardStep>('basic');
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Form data
  const [formData, setFormData] = useState<Partial<ScheduledJob>>({
    name: '',
    description: '',
    type: 'data_retention_purge',
    schedule: {
      frequency: 'daily',
      time: '02:00',
      timezone: 'America/New_York'
    },
    configuration: {},
    isActive: true,
    notifications: {
      onSuccess: false,
      onFailure: true,
      recipients: []
    },
    retryPolicy: {
      enabled: true,
      maxAttempts: 3,
      retryDelayMs: 300000
    }
  });

  useEffect(() => {
    if (!isEditMode || !jobId) return;
    let cancelled = false;
    scheduledJobsStorageRouter.getJobAsync(jobId).then(job => {
      if (cancelled) return;
      if (job) setFormData(job);
      else {
        showError('Error', 'Job not found');
        onCancel();
      }
    });
    return () => { cancelled = true; };
  }, [isEditMode, jobId, onCancel, showError]);

  // Get job type definition
  const getJobTypeDefinition = (type: JobType) => {
    return jobTypeDefinitions.find(def => def.type === type);
  };

  // Update form field
  const updateField = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // Update nested field
  const updateNestedField = (parent: string, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      [parent]: {
        ...(prev[parent as keyof typeof prev] as any),
        [field]: value
      }
    }));
  };

  // Validate current step
  const validateStep = (step: WizardStep): boolean => {
    const newErrors: Record<string, string> = {};

    switch (step) {
      case 'basic':
        if (!formData.name?.trim()) {
          newErrors.name = 'Job name is required';
        }
        if (!formData.type) {
          newErrors.type = 'Job type is required';
        }
        break;

      case 'schedule':
        if (!formData.schedule?.frequency) {
          newErrors.frequency = 'Frequency is required';
        }
        if (formData.schedule?.frequency !== 'once' && !formData.schedule?.time) {
          newErrors.time = 'Time is required';
        }
        if (formData.schedule?.frequency === 'custom' && !formData.schedule?.cronExpression) {
          newErrors.cronExpression = 'Cron expression is required';
        }
        break;

      case 'configuration':
        const jobTypeDef = getJobTypeDefinition(formData.type!);
        jobTypeDef?.configFields.forEach(field => {
          if (field.required && !formData.configuration?.[field.name]) {
            newErrors[`config_${field.name}`] = `${field.label} is required`;
          }
        });
        break;

      case 'notifications':
        if (formData.notifications?.recipients && formData.notifications.recipients.length === 0) {
          if (formData.notifications.onSuccess || formData.notifications.onFailure) {
            newErrors.recipients = 'At least one recipient is required when notifications are enabled';
          }
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Navigate to next step
  const handleNext = () => {
    if (!validateStep(currentStep)) return;

    const steps: WizardStep[] = ['basic', 'schedule', 'configuration', 'notifications'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex < steps.length - 1) {
      setCurrentStep(steps[currentIndex + 1]);
    }
  };

  // Navigate to previous step
  const handlePrevious = () => {
    const steps: WizardStep[] = ['basic', 'schedule', 'configuration', 'notifications'];
    const currentIndex = steps.indexOf(currentStep);
    if (currentIndex > 0) {
      setCurrentStep(steps[currentIndex - 1]);
    }
  };

  // Save job
  const handleSave = async () => {
    if (!validateStep(currentStep)) return;

    setIsSaving(true);
    try {
      const now = new Date().toISOString();
      const currentUser = 'user_admin';

      if (isEditMode && jobId) {
        const updated = await scheduledJobsStorageRouter.updateJobAsync(jobId, {
          ...formData,
          lastModifiedDate: now,
          lastModifiedBy: currentUser
        } as Partial<ScheduledJob>);
        if (updated) {
          success('Job Updated', `${formData.name} has been updated`);
          onSave();
        }
      } else {
        const newJob: Omit<ScheduledJob, 'id'> = {
          name: formData.name!,
          description: formData.description || '',
          type: formData.type!,
          schedule: formData.schedule!,
          configuration: formData.configuration || {},
          status: formData.isActive ? 'scheduled' : 'paused',
          isActive: formData.isActive ?? true,
          notifications: formData.notifications!,
          retryPolicy: formData.retryPolicy!,
          nextRun: scheduledJobsStorage.calculateNextRun(formData.schedule!),
          createdDate: now,
          createdBy: currentUser,
          lastModifiedDate: now,
          lastModifiedBy: currentUser
        };
        await scheduledJobsStorageRouter.createJobAsync(newJob);
        success('Job Created', `${formData.name} has been created`);
        onSave();
      }
    } catch (err) {
      showError('Error', 'Failed to save job');
    } finally {
      setIsSaving(false);
    }
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 'basic':
        return (
          <div className="space-y-4">
            <StandardInput
              label="Job Name"
              value={formData.name || ''}
              onChange={(e) => updateField('name', e.target.value)}
              placeholder="Enter job name"
              error={errors.name}
              required
            />

            <StandardInput
              label="Description"
              value={formData.description || ''}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Describe what this job does"
            />

            <StandardSelect
              label="Job Type"
              value={formData.type || ''}
              onChange={(e) => {
                updateField('type', e.target.value as JobType);
                updateField('configuration', {});
              }}
              options={jobTypeDefinitions.map(def => ({
                value: def.type,
                label: def.label
              }))}
              error={errors.type}
              required
            />

            {formData.type && (
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  {getJobTypeDefinition(formData.type)?.description}
                </p>
              </div>
            )}
          </div>
        );

      case 'schedule':
        return (
          <div className="space-y-4">
            <StandardSelect
              label="Frequency"
              value={formData.schedule?.frequency || ''}
              onChange={(e) => updateNestedField('schedule', 'frequency', e.target.value as JobFrequency)}
              options={[
                { value: 'once', label: 'Once (Run immediately)' },
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly' },
                { value: 'monthly', label: 'Monthly' },
                { value: 'custom', label: 'Custom (Cron)' }
              ]}
              error={errors.frequency}
              required
            />

            {formData.schedule?.frequency !== 'once' && (
              <>
                <StandardInput
                  label="Time"
                  type="time"
                  value={formData.schedule?.time || ''}
                  onChange={(e) => updateNestedField('schedule', 'time', e.target.value)}
                  helpText="Time to run the job (24-hour format)"
                  error={errors.time}
                  required
                />

                <StandardSelect
                  label="Timezone"
                  value={formData.schedule?.timezone || 'America/New_York'}
                  onChange={(e) => updateNestedField('schedule', 'timezone', e.target.value)}
                  options={[
                    { value: 'America/New_York', label: 'Eastern Time (ET)' },
                    { value: 'America/Chicago', label: 'Central Time (CT)' },
                    { value: 'America/Denver', label: 'Mountain Time (MT)' },
                    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
                    { value: 'UTC', label: 'UTC' }
                  ]}
                />
              </>
            )}

            {formData.schedule?.frequency === 'weekly' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Days of Week
                </label>
                <div className="flex flex-wrap gap-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => {
                    const isSelected = formData.schedule?.daysOfWeek?.includes(index);
                    return (
                      <button
                        key={day}
                        type="button"
                        onClick={() => {
                          const current = formData.schedule?.daysOfWeek || [];
                          const updated = isSelected
                            ? current.filter(d => d !== index)
                            : [...current, index];
                          updateNestedField('schedule', 'daysOfWeek', updated);
                        }}
                        className={`
                          px-3 py-2 rounded-md text-sm font-medium transition-colors
                          ${isSelected
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                          }
                        `}
                      >
                        {day}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {formData.schedule?.frequency === 'monthly' && (
              <StandardInput
                label="Day of Month"
                type="number"
                min={1}
                max={31}
                value={formData.schedule?.dayOfMonth || ''}
                onChange={(e) => updateNestedField('schedule', 'dayOfMonth', parseInt(e.target.value))}
                helpText="Day of the month to run (1-31)"
              />
            )}

            {formData.schedule?.frequency === 'custom' && (
              <StandardInput
                label="Cron Expression"
                value={formData.schedule?.cronExpression || ''}
                onChange={(e) => updateNestedField('schedule', 'cronExpression', e.target.value)}
                placeholder="0 2 * * *"
                helpText="Use standard cron syntax"
                error={errors.cronExpression}
                required
              />
            )}
          </div>
        );

      case 'configuration':
        const jobTypeDef = getJobTypeDefinition(formData.type!);

        if (!jobTypeDef || jobTypeDef.configFields.length === 0) {
          return (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No configuration required for this job type.
            </div>
          );
        }

        return (
          <div className="space-y-4">
            {jobTypeDef.configFields.map(field => {
              const value = formData.configuration?.[field.name];
              const error = errors[`config_${field.name}`];

              switch (field.type) {
                case 'text':
                case 'number':
                  return (
                    <StandardInput
                      key={field.name}
                      label={field.label}
                      type={field.type}
                      value={value ?? field.defaultValue ?? ''}
                      onChange={(e) => {
                        const val = field.type === 'number'
                          ? parseFloat(e.target.value)
                          : e.target.value;
                        updateNestedField('configuration', field.name, val);
                      }}
                      helpText={field.helpText}
                      error={error}
                      required={field.required}
                    />
                  );

                case 'select':
                  return (
                    <StandardSelect
                      key={field.name}
                      label={field.label}
                      value={value ?? field.defaultValue ?? ''}
                      onChange={(e) => updateNestedField('configuration', field.name, e.target.value)}
                      options={field.options || []}
                      helpText={field.helpText}
                      error={error}
                      required={field.required}
                    />
                  );

                case 'checkbox':
                  return (
                    <div key={field.name} className="flex items-start gap-2">
                      <input
                        type="checkbox"
                        id={field.name}
                        checked={value ?? field.defaultValue ?? false}
                        onChange={(e) => updateNestedField('configuration', field.name, e.target.checked)}
                        className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <label htmlFor={field.name} className="text-sm">
                        <div className="font-medium text-gray-700 dark:text-gray-300">
                          {field.label}
                        </div>
                        {field.helpText && (
                          <div className="text-gray-500 dark:text-gray-400">
                            {field.helpText}
                          </div>
                        )}
                      </label>
                    </div>
                  );

                case 'textarea':
                  return (
                    <div key={field.name}>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </label>
                      <textarea
                        value={value ?? field.defaultValue ?? ''}
                        onChange={(e) => updateNestedField('configuration', field.name, e.target.value)}
                        rows={3}
                        className="w-full px-4 py-2 rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      />
                      {field.helpText && (
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                          {field.helpText}
                        </p>
                      )}
                      {error && (
                        <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                          {error}
                        </p>
                      )}
                    </div>
                  );

                default:
                  return null;
              }
            })}
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-4">
            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="notifyOnSuccess"
                checked={formData.notifications?.onSuccess || false}
                onChange={(e) => updateNestedField('notifications', 'onSuccess', e.target.checked)}
                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="notifyOnSuccess" className="text-sm">
                <div className="font-medium text-gray-700 dark:text-gray-300">
                  Notify on Success
                </div>
                <div className="text-gray-500 dark:text-gray-400">
                  Send notification when job completes successfully
                </div>
              </label>
            </div>

            <div className="flex items-start gap-2">
              <input
                type="checkbox"
                id="notifyOnFailure"
                checked={formData.notifications?.onFailure || false}
                onChange={(e) => updateNestedField('notifications', 'onFailure', e.target.checked)}
                className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <label htmlFor="notifyOnFailure" className="text-sm">
                <div className="font-medium text-gray-700 dark:text-gray-300">
                  Notify on Failure
                </div>
                <div className="text-gray-500 dark:text-gray-400">
                  Send notification when job fails
                </div>
              </label>
            </div>

            <StandardInput
              label="Recipients"
              value={formData.notifications?.recipients?.join(', ') || ''}
              onChange={(e) => {
                const recipients = e.target.value.split(',').map(r => r.trim()).filter(Boolean);
                updateNestedField('notifications', 'recipients', recipients);
              }}
              placeholder="email1@example.com, email2@example.com"
              helpText="Comma-separated list of email addresses"
              error={errors.recipients}
            />

            <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
              <h3 className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-3">
                Retry Policy
              </h3>

              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="retryEnabled"
                    checked={formData.retryPolicy?.enabled || false}
                    onChange={(e) => updateNestedField('retryPolicy', 'enabled', e.target.checked)}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label htmlFor="retryEnabled" className="text-sm">
                    <div className="font-medium text-gray-700 dark:text-gray-300">
                      Enable Retry on Failure
                    </div>
                    <div className="text-gray-500 dark:text-gray-400">
                      Automatically retry failed jobs
                    </div>
                  </label>
                </div>

                {formData.retryPolicy?.enabled && (
                  <>
                    <StandardInput
                      label="Max Retry Attempts"
                      type="number"
                      min={1}
                      max={10}
                      value={formData.retryPolicy?.maxAttempts || 3}
                      onChange={(e) => updateNestedField('retryPolicy', 'maxAttempts', parseInt(e.target.value))}
                    />

                    <StandardSelect
                      label="Retry Delay"
                      value={formData.retryPolicy?.retryDelayMs || 300000}
                      onChange={(e) => updateNestedField('retryPolicy', 'retryDelayMs', parseInt(e.target.value))}
                      options={[
                        { value: 60000, label: '1 minute' },
                        { value: 180000, label: '3 minutes' },
                        { value: 300000, label: '5 minutes' },
                        { value: 600000, label: '10 minutes' },
                        { value: 1800000, label: '30 minutes' }
                      ]}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Step progress
  const steps: { key: WizardStep; label: string; number: number }[] = [
    { key: 'basic', label: 'Basic Info', number: 1 },
    { key: 'schedule', label: 'Schedule', number: 2 },
    { key: 'configuration', label: 'Configuration', number: 3 },
    { key: 'notifications', label: 'Notifications', number: 4 }
  ];

  const currentStepIndex = steps.findIndex(s => s.key === currentStep);

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <StandardButton
          variant="ghost"
          leftIcon={<ArrowLeft size={16} />}
          onClick={onCancel}
        >
          Back
        </StandardButton>
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {isEditMode ? 'Edit Scheduled Job' : 'Create Scheduled Job'}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            {isEditMode ? 'Update job configuration' : 'Set up a new automated job'}
          </p>
        </div>
      </div>

      {/* Progress Steps */}
      <StandardCard>
        <StandardCard.Body>
          <div className="flex items-center justify-between">
            {steps.map((step, index) => (
              <React.Fragment key={step.key}>
                <div className="flex items-center gap-3">
                  <div className={`
                    w-10 h-10 rounded-full flex items-center justify-center font-medium transition-colors
                    ${currentStepIndex >= index
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                    }
                  `}>
                    {step.number}
                  </div>
                  <div className="hidden sm:block">
                    <div className={`text-sm font-medium ${
                      currentStepIndex >= index
                        ? 'text-gray-900 dark:text-gray-100'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {step.label}
                    </div>
                  </div>
                </div>
                {index < steps.length - 1 && (
                  <div className={`flex-1 h-0.5 mx-4 ${
                    currentStepIndex > index
                      ? 'bg-blue-600'
                      : 'bg-gray-200 dark:bg-gray-700'
                  }`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </StandardCard.Body>
      </StandardCard>

      {/* Step Content */}
      <StandardCard>
        <StandardCard.Header title={steps[currentStepIndex].label} />
        <StandardCard.Body>
          {renderStepContent()}
        </StandardCard.Body>
        <StandardCard.Footer>
          <div className="flex items-center justify-between w-full">
            <StandardButton
              variant="secondary"
              onClick={handlePrevious}
              disabled={currentStepIndex === 0}
            >
              Previous
            </StandardButton>

            <div className="flex items-center gap-2">
              <StandardButton
                variant="ghost"
                leftIcon={<X size={16} />}
                onClick={onCancel}
              >
                Cancel
              </StandardButton>

              {currentStepIndex === steps.length - 1 ? (
                <StandardButton
                  variant="primary"
                  leftIcon={<Save size={16} />}
                  onClick={handleSave}
                  isLoading={isSaving}
                >
                  {isEditMode ? 'Update Job' : 'Create Job'}
                </StandardButton>
              ) : (
                <StandardButton
                  variant="primary"
                  rightIcon={<ArrowRight size={16} />}
                  onClick={handleNext}
                >
                  Next
                </StandardButton>
              )}
            </div>
          </div>
        </StandardCard.Footer>
      </StandardCard>
    </div>
  );
};

export default ScheduledJobWizard;
